/**
 * Admin Messaging Mediation at `/admin/messaging`.
 *
 * Lists every Provider ↔ Client thread on the platform with filters for
 * reported / quarantined, opens a side-panel showing the full message
 * history of any thread, and offers four admin actions per thread:
 *   • Quarantine / Un-quarantine
 *   • Soft-delete a single message
 *   • Add an internal note (invisible to participants)
 *   • Refresh
 *
 * Every action is logged server-side to `db.thread_audit` — the admin
 * console will show the audit trail at /admin/messaging/audit if you
 * ever need to prove who looked at what, when.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChatCircleDotsIcon as ChatCircleDots, WarningIcon as Warning, ShieldWarningIcon as ShieldWarning, NoteIcon as Note, TrashIcon as Trash, ArrowsClockwiseIcon as ArrowsClockwise, XIcon as XIcon, SparkleIcon as Sparkle, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";

const TABS = [
  { key: "all", label: "All threads" },
  { key: "reported", label: "Reported" },
  { key: "quarantined", label: "Quarantined" },
];

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminMessaging() {
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [threads, setThreads] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (tab !== "all") params.set("status", tab);
    if (search.trim()) params.set("q", search.trim());
    api.get(`/admin/messaging/threads?${params.toString()}`)
      .then((r) => setThreads(r.data?.threads || []))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load threads");
        setThreads([]);
      });
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-8 font-manrope text-ink">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
              Admin · Mediation
            </p>
            <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight">
              Provider &amp; Client Messages
            </h1>
            <p className="text-sm text-[#666] mt-2 max-w-2xl">
              Every action on this page is logged. Visit{" "}
              <Link to="/admin/console" className="underline">the admin console</Link>{" "}
              to see the full audit trail. Per Privacy Policy §2.4a, only access threads in
              response to a report or open dispute.
            </p>
          </div>
          <Button variant="outline" onClick={load} className="rounded-full md:shrink-0" data-testid="admin-msg-refresh">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center mb-5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`admin-msg-tab-${t.key}`}
              className={
                "rounded-full px-4 py-1.5 text-sm transition-colors " +
                (tab === t.key
                  ? "bg-[#0E0E0E] text-white"
                  : "bg-white text-[#3F3F3F] border border-[#E5E5E5] hover:bg-[#F4F4F4]")
              }
            >
              {t.label}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search provider or client name…"
              className="w-64"
              data-testid="admin-msg-search-input"
            />
            <Button variant="outline" onClick={load} data-testid="admin-msg-search-btn">Search</Button>
          </div>
        </div>

        {/* List */}
        {threads === null ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : threads.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center" data-testid="admin-msg-empty">
            <ChatCircleDots size={36} className="mx-auto text-[#9b9b9b]" />
            <p className="mt-3 font-spectral text-xl">No threads match this filter.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-msg-list">
            {threads.map((t) => (
              <ThreadRow key={t.id} t={t} onOpen={() => setOpenId(t.id)} />
            ))}
          </div>
        )}
      </div>

      {openId && (
        <ThreadPanel
          threadId={openId}
          onClose={() => setOpenId(null)}
          onMutated={load}
        />
      )}
    </AppShell>
  );
}

function ThreadRow({ t, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`admin-msg-row-${t.id}`}
      className="w-full text-left bg-white hover:bg-[#FBFBFB] border border-[#E5E5E5] hover:border-[#D5D5D5] rounded-xl p-4 transition-colors flex items-center gap-3"
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          {t.quarantined && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded-full bg-[#C1272D]/15 text-[#C1272D] px-2 py-0.5 font-semibold">
              <ShieldWarning size={10} weight="fill" /> Quarantined
            </span>
          )}
          {t.report_count > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 font-semibold">
              <Warning size={10} weight="fill" /> {t.report_count} {t.report_count === 1 ? "report" : "reports"}
            </span>
          )}
        </div>
        <p className="font-spectral text-base mt-1 truncate">
          <strong>{t.provider_name || "Provider"}</strong>
          <span className="text-[#999] mx-1.5">↔</span>
          {t.client_name || "Client"}
        </p>
        <p className="text-sm text-[#666] truncate mt-0.5">
          {t.last_message_from === "system" && (
            <span className="text-[#999] mr-1">System:</span>
          )}
          {t.last_message_preview || "(no messages yet)"}
        </p>
      </div>
      <span className="text-xs text-[#888] shrink-0">{fmt(t.last_message_at)}</span>
    </button>
  );
}

function ThreadPanel({ threadId, onClose, onMutated }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [noteBody, setNoteBody] = useState("");

  const load = () => {
    api.get(`/admin/messaging/threads/${threadId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load thread");
        onClose();
      });
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [threadId]);

  const t = data?.thread;
  const messages = data?.messages || [];
  const notes = data?.notes || [];

  const quarantine = async () => {
    const reason = window.prompt(
      "Brief reason for quarantine (visible only to admins):",
      "Reviewing a report — temporary hold."
    );
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post(`/admin/messaging/threads/${threadId}/quarantine`, { reason });
      toast.success("Thread quarantined");
      load();
      onMutated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  const unquarantine = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/messaging/threads/${threadId}/unquarantine`);
      toast.success("Thread restored to both participants");
      load();
      onMutated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  const removeMessage = async (messageId) => {
    if (!window.confirm("Remove this message from both sides? This is irreversible and audit-logged.")) return;
    try {
      await api.delete(`/admin/messaging/threads/${threadId}/messages/${messageId}`);
      toast.success("Message removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const addNote = async () => {
    if (!noteBody.trim()) return;
    try {
      await api.post(`/admin/messaging/threads/${threadId}/notes`, { body: noteBody.trim() });
      setNoteBody("");
      toast.success("Note saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
      <div
        className="w-full md:max-w-2xl bg-white h-full overflow-y-auto p-6 md:p-8 relative shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-msg-panel"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F4F4F4]"
          data-testid="admin-msg-panel-close"
          aria-label="Close"
        >
          <XIcon size={18} />
        </button>

        {!data ? (
          <div className="space-y-3 mt-6">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B]">Thread</p>
            <h2 className="font-spectral text-xl mt-1">
              <strong>{t.provider_name}</strong>
              <span className="text-[#999] mx-2">↔</span>
              {t.client_name}
            </h2>
            <p className="text-xs text-[#888] mt-1">
              {messages.length} {messages.length === 1 ? "message" : "messages"} · started {fmt(t.created_at)}
              {t.report_count > 0 && <span className="text-amber-700"> · {t.report_count} report{t.report_count === 1 ? "" : "s"}</span>}
            </p>

            {t.quarantined && (
              <div className="mt-4 px-4 py-3 rounded-lg bg-[#C1272D]/10 border border-[#C1272D]/20 text-sm">
                <p className="font-semibold text-[#C1272D] flex items-center gap-1.5">
                  <ShieldWarning size={14} weight="fill" /> Quarantined
                </p>
                {t.quarantine_reason && <p className="text-[#666] mt-1">{t.quarantine_reason}</p>}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              {t.quarantined ? (
                <Button size="sm" variant="outline" onClick={unquarantine} disabled={busy} data-testid="admin-msg-unquarantine">
                  <CheckCircle size={14} className="mr-1.5" /> Restore to participants
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={quarantine} disabled={busy} className="text-[#C1272D]" data-testid="admin-msg-quarantine">
                  <ShieldWarning size={14} className="mr-1.5" /> Quarantine
                </Button>
              )}
            </div>

            {/* Messages */}
            <div className="mt-6 space-y-3" data-testid="admin-msg-messages">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={
                    "rounded-xl p-3 border " +
                    (m.deleted
                      ? "bg-[#F4F4F4] border-[#E5E5E5] text-[#888] italic"
                      : m.sender_side === "system"
                      ? "bg-amber-50 border-amber-200"
                      : m.sender_side === "provider"
                      ? "bg-[#FAFAFA] border-[#E5E5E5]"
                      : "bg-white border-[#E5E5E5]")
                  }
                  data-testid={`admin-msg-message-${m.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] uppercase tracking-wider text-[#888]">
                      {m.sender_side === "system" ? "System event" : `From ${m.sender_side}`}
                      {m.pii_warning && (
                        <span className="ml-2 text-amber-700">⚠ contact-info detected</span>
                      )}
                    </p>
                    <span className="text-[10px] text-[#999]">{fmt(m.created_at)}</span>
                  </div>
                  <p className="text-sm mt-1.5 whitespace-pre-wrap break-words">{m.body}</p>
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="grid grid-cols-3 gap-1.5 mt-2">
                      {m.attachments.map((url, i) => (
                        <a key={i} href={resolveUrl(url)} target="_blank" rel="noreferrer" className="rounded-md overflow-hidden block">
                          <img src={resolveUrl(url)} alt="" className="w-full h-20 object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  {!m.deleted && m.sender_side !== "system" && (
                    <button
                      type="button"
                      onClick={() => removeMessage(m.id)}
                      className="mt-2 text-xs text-[#C1272D] hover:underline inline-flex items-center gap-1"
                      data-testid={`admin-msg-delete-${m.id}`}
                    >
                      <Trash size={11} /> Remove this message
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Internal notes */}
            <div className="mt-8 border-t border-[#EEE] pt-6" data-testid="admin-msg-notes">
              <p className="text-xs uppercase tracking-wider text-[#666] font-bold mb-2 flex items-center gap-1.5">
                <Note size={14} /> Internal admin notes
              </p>
              {notes.length === 0 ? (
                <p className="text-xs text-[#888] italic">No notes yet.</p>
              ) : (
                <ul className="space-y-2">
                  {notes.map((n) => (
                    <li key={n.id} className="text-sm bg-[#FFFBEA] border border-[#F2E3A1] rounded-md p-2.5">
                      <p className="whitespace-pre-wrap">{n.body}</p>
                      <p className="text-[10px] text-[#888] mt-1">— {n.admin_email || n.admin_id}, {fmt(n.created_at)}</p>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3">
                <Textarea
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  rows={2}
                  placeholder="Add an internal note (not visible to participants)…"
                  data-testid="admin-msg-note-input"
                />
                <Button size="sm" className="mt-2" onClick={addNote} disabled={!noteBody.trim()} data-testid="admin-msg-note-save">
                  <Note size={12} className="mr-1.5" /> Save note
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
